import type { HecProduct } from "~/hec/types/product";
import type { C_ProductInfo, Image, ProductSwatch } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";

import { useLocationState } from "../../../location-state/location-state";

export type ProductDetailsHystoryState = {
  // C_ProductInfo is extended product type
  product: SerializeFrom<C_ProductInfo>;
  swatch: ProductSwatch;
  image?: Image;
  orderable?: boolean;
  quickNav?: SerializeFrom<C_ProductInfo>[];
  totalProducts?: number;
  direction?: "forward" | "back";
};

/**
 * Lightweight nav-state seed carried from a list/rec tile click (the
 * `HecProduct` the user just clicked). The PDP renders the "important half"
 * (hero image, title, brand, SKU, ratings, price, eligibility/free-shipping
 * badges) optimistically from this seed in the Suspense fallbacks, instead of
 * a bare skeleton, while the system-of-record SCAPI `_product` streams in.
 *
 * Distinct from the quick-nav `product`+`image` envelope above: a tile click
 * sets only `tileProduct`, so `useProductDetailsHistoryState` (which requires
 * `product`+`image`) still returns undefined and the quick-nav UI stays off.
 */
export type ProductTileSeedState = {
  tileProduct?: SerializeFrom<HecProduct>;
};
export const isProductDetailsHystoryState = (
  state: any,
): state is ProductDetailsHystoryState => {
  return (
    state &&
    typeof state === "object" &&
    "product" in state &&
    typeof state.product === "object" &&
    state.product !== null &&
    "id" in state.product &&
    "image" in state
  );
};
export const useProductDetailsHistoryState = () => {
  const [state, set] = useLocationState<ProductDetailsHystoryState>();
  if (isProductDetailsHystoryState(state)) return [state, set] as const;
  return [undefined, set] as const;
};

/**
 * Read-only accessor for the tile seed (see {@link ProductTileSeedState}).
 * Reuses `useLocationState` so the seed is cleared on `beforeunload` (a full
 * refresh is a document request that awaits the product fully — no skeleton —
 * so the seed isn't needed and clearing it avoids a hydration mismatch).
 */
export const useProductTileSeed = (): SerializeFrom<HecProduct> | undefined => {
  const [state] = useLocationState<ProductTileSeedState>();
  return state?.tileProduct;
};
